import React, { useState, useEffect } from 'react';
import { Table, Button, Tag, Space, Typography, message } from 'antd';
import { EyeOutlined, EnvironmentOutlined, CalendarOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { jobAPI, Job, JobListResponse } from '../services/api';
import './JobList.css';

const { Text } = Typography;

interface JobListProps {
  searchFilters: {
    keyword: string;
    country: string;
    city: string;
    state: string;
    recruitingOnly: boolean;
  };
  onJobSelect: (jobId: number) => void;
}

const JobList: React.FC<JobListProps> = ({ searchFilters, onJobSelect }) => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const limit = 10;

  const fetchJobs = async (page: number = 1) => {
    try {
      setLoading(true);
      
      const skip = (page - 1) * limit;
      let response: JobListResponse;

      // 根据搜索条件选择API
      if (searchFilters.keyword || searchFilters.country || searchFilters.city || searchFilters.state) {
        response = await jobAPI.searchJobs(
          searchFilters.keyword,
          searchFilters.country,
          searchFilters.city,
          searchFilters.state,
          undefined, // location_query
          searchFilters.recruitingOnly,
          skip,
          limit
        );
      } else if (searchFilters.recruitingOnly) {
        response = await jobAPI.getRecruitingJobs(skip, limit);
      } else {
        response = await jobAPI.getJobs(skip, limit, false);
      }

      setJobs(response.jobs);
      setTotal(response.total);
      
    } catch (err) {
      message.error(err instanceof Error ? err.message : '加载岗位失败');
    } finally {
      setLoading(false);
    }
  };

  // 搜索条件变化时重新加载
  useEffect(() => {
    setCurrentPage(1);
    fetchJobs(1);
  }, [searchFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  // 解析公司信息
  const parseCompanyInfo = (companyInfo: string) => {
    const parts = companyInfo.split('；');
    const company: any = {};
    
    parts.forEach(part => {
      if (part.includes('公司名称：')) {
        company.name = part.replace('公司名称：', '');
      } else if (part.includes('公司规模：')) {
        company.size = part.replace('公司规模：', '');
      } else if (part.includes('行业：')) {
        company.industry = part.replace('行业：', '');
      }
    });
    
    return company;
  };

  // 格式化日期
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN');
  };

  // 表格列定义
  const columns: ColumnsType<Job> = [
    {
      title: '职位信息',
      dataIndex: 'job_title',
      key: 'job_title',
      width: 200,
      render: (title: string, record: Job) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: '16px', marginBottom: '4px' }}>
            {title}
          </div>
          <div style={{ color: '#666', fontSize: '14px' }}>
            {record.salary_range || '薪资面议'}
          </div>
        </div>
      ),
    },
    {
      title: '公司信息',
      dataIndex: 'company_info',
      key: 'company_info',
      width: 180,
      render: (companyInfo: string) => {
        const company = parseCompanyInfo(companyInfo);
        return (
          <div>
            <div style={{ fontWeight: 500, marginBottom: '4px' }}>
              {company.name}
            </div>
            <div style={{ color: '#666', fontSize: '12px' }}>
              {company.size} • {company.industry}
            </div>
          </div>
        );
      },
    },
        {
          title: '工作地点',
          key: 'location',
          width: 180,
          render: (_, record: Job) => {
            const locationParts = [];
            if (record.country) locationParts.push(record.country);
            if (record.state) locationParts.push(record.state);
            if (record.city) locationParts.push(record.city);
            const locationText = locationParts.join(' · ');
            
            return (
              <div>
                <div style={{ marginBottom: '4px' }}>
                  <EnvironmentOutlined style={{ marginRight: '4px', color: '#1890ff' }} />
                  {locationText}
                </div>
                {record.work_location_detail && (
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {record.work_location_detail}
                  </Text>
                )}
              </div>
            );
          }
        },
    {
      title: '要求',
      key: 'requirements',
      width: 180,
      render: (_, record: Job) => (
        <Space direction="vertical" size="small">
          {record.education_requirement && (
            <Tag color="blue">{record.education_requirement}</Tag>
          )}
          {record.experience_years && (
            <Tag color="green">{record.experience_years}</Tag>
          )}
        </Space>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={status === '招聘中' ? 'success' : 'default'}>
          {status}
        </Tag>
      ),
    },
    {
      title: '发布时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date: string) => (
        <div style={{ fontSize: '12px', color: '#666' }}>
          <CalendarOutlined style={{ marginRight: '4px' }} />
          {formatDate(date)}
        </div>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record: Job) => (
        <Button
          type="primary"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => onJobSelect(record.id)}
        >
          查看详情
        </Button>
      ),
    },
  ];

  const handleTableChange = (pagination: any) => {
    setCurrentPage(pagination.current);
    fetchJobs(pagination.current);
  };

  return (
    <div className="job-list-container">
      <div className="job-list-header">
               {(searchFilters.keyword || searchFilters.country || searchFilters.city || searchFilters.state) && (
                 <div className="search-summary">
                   {searchFilters.keyword && (
                     <Tag color="blue">关键词: {searchFilters.keyword}</Tag>
                   )}
                   {searchFilters.country && (
                     <Tag color="green">国家: {searchFilters.country}</Tag>
                   )}
                   {searchFilters.city && (
                     <Tag color="orange">城市: {searchFilters.city}</Tag>
                   )}
                   {searchFilters.state && (
                     <Tag color="purple">州/省: {searchFilters.state}</Tag>
                   )}
            {searchFilters.recruitingOnly && (
              <Tag color="orange">仅招聘中</Tag>
            )}
          </div>
        )}
      </div>

      <Table
        columns={columns}
        dataSource={jobs}
        loading={loading}
        rowKey="id"
        pagination={{
          current: currentPage,
          pageSize: limit,
          total: total,
          showSizeChanger: false,
          showQuickJumper: true,
          showTotal: (total: number, range: [number, number]) => 
            `显示 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
          onChange: (page: number) => {
            setCurrentPage(page);
            fetchJobs(page);
          }
        }}
        scroll={{ x: 800 }}
        locale={{
          emptyText: (
            <div style={{ textAlign: 'center', padding: '50px 0' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
              <h3>暂无匹配的岗位</h3>
              <p style={{ color: '#666' }}>试试调整搜索条件或稍后再来看看</p>
            </div>
          )
        }}
        onChange={handleTableChange}
      />
    </div>
  );
};

export default JobList;
